import React from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Stack, Box, Flex, Text, xcss, Anchor } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';

type HTV2LearningCardProps = {
	title: MessageDescriptor;
	subtitle: MessageDescriptor;
	link: string;
	icon: React.ReactNode;
	onboardingPeriodDay: number;
};

const anchorStyles = xcss({
	color: 'color.text',
	border: `1px solid ${token('color.border')}`,
	borderRadius: '8px',
	padding: 'space.200',
	margin: 'space.0',
	textAlign: 'left',
	backgroundColor: 'elevation.surface',
	textDecoration: 'none',

	':hover': {
		backgroundColor: 'elevation.surface.hovered',
		textDecoration: 'none',
	},
	':active': {
		backgroundColor: 'elevation.surface.pressed',
	},
});

export const HTV2XflowLearningOpportunityCard = ({
	title,
	subtitle,
	link,
	icon,
	onboardingPeriodDay,
}: HTV2LearningCardProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'homeOnboardingTakeoverV2learningOpportunityCta',
				attributes: {
					isXflow: true,
					learningOpportunity: title.defaultMessage,
					onboardingPeriodDay,
				},
				source: 'onboarding',
			},
		}).fire();
	};

	return (
		<Anchor
			onClick={handleClick}
			xcss={anchorStyles}
			href={link}
			target="_blank"
			rel="noopener noreferrer"
		>
			<Flex direction="row" gap="space.200">
				<Box>{icon}</Box>
				<Stack space="space.100">
					<Heading size="xsmall">
						<FormattedMessage {...title} />
					</Heading>
					<Text color="color.text.subtle" size="small">
						<FormattedMessage {...subtitle} />
					</Text>
				</Stack>
			</Flex>
		</Anchor>
	);
};
